import React, { useEffect, useState, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaFolder,
  FaFolderPlus,
  FaFileAlt,
  FaDownload,
  FaTrash,
  FaEye,
  FaFileUpload,
  FaSearch,
  FaTimes,
  FaSpinner,
  FaCloudUploadAlt,
  FaRegFolderOpen,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaFileCode,
} from "react-icons/fa";
import {
  getFolders,
  createFolder,
  uploadFiles,
  deleteFolder,
  deleteFile,
  downloadFile,
  downloadFolder,
  getSchoolExcelUrl,
  getTeacherExcelUrl,
} from "../../services/fileManagerService.jsx";
import { getSchools } from "../../services/schoolService.jsx";
import DeleteFolderModal from "./DeleteFolderModal.jsx";
import './MangerLayer.css';

const MangerLayer = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [folders, setFolders] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [hoveredItem, setHoveredItem] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null,
    name: "",
    type: "Folder",
    loading: false,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSchools();
    setAnimateIn(true);
  }, []);

  useEffect(() => {
    if (schoolId) fetchFolders();
  }, [schoolId]);

  const loadSchools = async () => {
    try {
      const res = await getSchools();
      if (res?.status && res.data.length > 0) {
        setSchools(res.data);
        setSchoolId(res.data[0].id);
      } else if (res?.data?.length > 0) {
        setSchools(res.data);
        setSchoolId(res.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load schools:", error);
      toast.error("Failed to load schools");
    }
  };

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const res = await getFolders(schoolId);
      if (res?.status) {
        setFolders(res.data);
      } else if (Array.isArray(res)) {
        setFolders(res);
      } else {
        setFolders([]);
      }
    } catch (error) {
      console.error("Failed to fetch folders:", error);
      toast.error("Failed to load folders");
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FaFileImage />;
    if (['pdf'].includes(ext)) return <FaFilePdf />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FaFileExcel />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FaFileArchive />;
    if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml'].includes(ext)) return <FaFileCode />;
    return <FaFileAlt />;
  };

  const triggerDownload = (data, fileName, mimeType = "application/octet-stream") => {
    try {
      const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success(`Downloading ${fileName}`);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Could not process download");
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return toast.warn("Enter folder name");
    setCreatingFolder(true);
    try {
      const res = await createFolder({ school_id: schoolId, name: folderName });
      if (res?.status) {
        toast.success("Folder created successfully");
        setFolderName("");
        fetchFolders();
        setExpandedFolders(prev => ({ ...prev, [res.data?.id]: true }));
      } else {
        toast.error(res?.message || "Failed to create folder");
      }
    } catch (error) {
      console.error("Create folder error:", error);
      toast.error("Error creating folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
      toast.info(`${selectedFiles.length} file(s) selected`);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFolder) return toast.warn("Please select a folder first");
    if (!files.length) return toast.warn("Please select files to upload");

    setUploading(true);
    try {
      const res = await uploadFiles({ school_id: schoolId, folder_id: selectedFolder, files });
      if (res?.status) {
        toast.success(`${files.length} file(s) uploaded successfully`);
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchFolders();
        setExpandedFolders(prev => ({ ...prev, [selectedFolder]: true }));
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading files");
    } finally {
      setUploading(false);
    }
  };

    const handleDeleteFolder = (e, id, folderName) => {
      e.stopPropagation();
      setDeleteModal({
        isOpen: true,
        id,
        name: folderName,
        type: "Folder",
        loading: false,
      });
    };

      const handleDeleteFile = (id, fileName) => {
        setDeleteModal({
          isOpen: true,
          id,
          name: fileName,
          type: "File",
          loading: false,
        });
      };

      const handleConfirmDelete = async () => {
        if (!deleteModal.id) return;
        setDeleteModal((prev) => ({ ...prev, loading: true }));
        try {
            if (deleteModal.type === "Folder") {
              const res = await deleteFolder(deleteModal.id);
              if (res?.status) {
                toast.success(`Folder "${deleteModal.name}" deleted`);
                if (selectedFolder === deleteModal.id) setSelectedFolder(null);
                fetchFolders();
              } else {
                toast.error(res?.message || "Failed to delete folder");
              }
            } else {
              const res = await deleteFile(deleteModal.id);
              if (res?.status) {
                toast.success(`File "${deleteModal.name}" deleted`);
                fetchFolders();
              } else {
                toast.error(res?.message || "Failed to delete file");
              }
            }
          } catch (error) {
            console.error("Delete error:", error);
            toast.error(`Error deleting ${deleteModal.type.toLowerCase()}`);
          } finally {
            setDeleteModal({ isOpen: false, id: null, name: "", type: "Folder", loading: false });
          }
        };

        const handleDownloadFile = async (fileId, fileName) => {
          try {
            const res = await downloadFile(fileId);
            if (res?.data) {
              const mime = res.headers?.["content-type"] || "application/octet-stream";
              triggerDownload(new Blob([res.data], { type: mime }), fileName, mime);
            } else {
              toast.error("No file data received");
            }
          } catch (error) {
            console.error("Download file error:", error);
            toast.error("Failed to download file");
          }
        };

        const handleDownloadFolder = async (folderId, folderName) => {
          try {
            const res = await downloadFolder(folderId);
            if (res?.data) {
              const mime = res.headers?.["content-type"] || "application/zip";
              triggerDownload(new Blob([res.data], { type: mime }), `${folderName}.zip`, mime);
            } else {
              toast.error("No folder data received");
            }
          } catch (error) {
            console.error("Download folder error:", error);
            toast.error("Failed to download folder");
          }
        };

        const formatFileSize = (bytes) => {
          if (!bytes) return '0 B';
          const k = 1024;
          const sizes = ['B', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        };

        const filteredFolders = folders.filter(folder =>
          folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          folder.files?.some(file => file.file_name.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const selectedFolderData = folders.find(f => f.id === selectedFolder);

        return (
          <div className={`premium-manager ${animateIn ? 'fade-in' : ''}`}>


            <ToastContainer position="top-right" autoClose={1000} hideProgressBar={false} newestOnTop />

            {/* Premium Header */}
            <div className="premium-header">
              <div className="header-gradient">
                <div className="logo-area">
                  <div className="logo-icon">
                    <FaRegFolderOpen />
                  </div>
                  <div className="logo-text">
                    <h5>FileManager</h5>
                    <p>Secure Document Management</p>
                  </div>
                </div>
                <div className="header-controls">
                  <div className="premium-search">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search files and folders..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm("")}>
                        <FaTimes />
                      </button>
                    )}
                  </div>
                  <select
                    className="premium-select"
                    value={schoolId}
                    onChange={(e) => setSchoolId(e.target.value)}
                  >
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="premium-main">
              {/* Premium Sidebar */}
              <div className="premium-sidebar">
                <div className="sidebar-header">
                  <h3>Storage</h3>
                </div>
                <button
                  className={`sidebar-item ${!selectedFolder ? 'active' : ''}`}
                  onClick={() => setSelectedFolder(null)}
                >
                  <FaRegFolderOpen className="sidebar-icon" />
                  <span>All Folders</span>
                  <span className="sidebar-badge">{folders.length}</span>
                </button>
                {folders.map(folder => (
                  <button
                    key={folder.id}
                    className={`sidebar-item ${selectedFolder === folder.id ? 'active' : ''}`}
                    onClick={() => setSelectedFolder(folder.id)}
                    onMouseEnter={() => setHoveredItem(folder.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <FaFolder className="sidebar-icon" />
                    <span>{folder.name}</span>
                    <span className="sidebar-badge">{folder.files?.length || 0}</span>
                  </button>
                ))}
              </div>

              {/* Content Area */}
              <div className="premium-content">
                {/* Action Buttons */}
                <div className="premium-actions">
                  <div className="action-group">
                    <button
                      className="premium-btn btn-primary"
                      onClick={() => document.getElementById('premium-folder-modal').classList.add('show')}
                    >
                      <FaFolderPlus /> New Folder
                    </button>
                    <button
                      className="premium-btn btn-success"
                      onClick={() => {
                        if (selectedFolder) {
                          fileInputRef.current?.click();
                        } else {
                          toast.warn("Please select a folder first");
                        }
                      }}
                    >
                      <FaFileUpload /> Upload Files
                    </button>
                    <button
                      className="premium-btn"
                      style={{ background: "#10b981", color: "#ffffff" }}
                      onClick={() => {
                        if (schoolId) {
                          toast.info("Exporting School Uploaded Excel...");
                          window.open(getSchoolExcelUrl(schoolId), "_blank");
                        } else {
                          toast.warn("Please select a school first");
                        }
                      }}
                      title="Export School Uploaded List (Excel)"
                    >
                      <FaFileExcel /> School List Export
                    </button>
                    <button
                      className="premium-btn"
                      style={{ background: "#4f46e5", color: "#ffffff" }}
                      onClick={() => {
                        if (schoolId) {
                          toast.info("Exporting App/Teacher Uploaded Excel...");
                          window.open(getTeacherExcelUrl(schoolId), "_blank");
                        } else {
                          toast.warn("Please select a school first");
                        }
                      }}
                      title="Export App/Teacher Uploaded List (Excel)"
                    >
                      <FaFileExcel /> App List Export
                    </button>
                    <input type="file" multiple ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
                  </div>
                  <div className="view-toggle">
                    <button
                      className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      List
                    </button>
                    <button
                      className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                    >
                      Grid
                    </button>
                  </div>
                </div>

                {/* Upload Queue */}
                {files.length > 0 && (
                  <div className="upload-queue-premium">
                    <div className="queue-header-premium">
                      <strong>📦 Upload Queue ({files.length} files)</strong>
                      <button
                        onClick={() => setFiles([])}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="queue-items-premium">
                      {files.map((file, idx) => (
                        <div key={idx} className="queue-item-premium">
                          {getFileIcon(file.name)}
                          <span style={{ flex: 1 }}>{file.name}</span>
                          <small>{formatFileSize(file.size)}</small>
                          <FaTimes
                            onClick={() => removeFile(idx)}
                            style={{ cursor: 'pointer', color: '#dc2626' }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      className="premium-btn btn-success"
                      onClick={handleUpload}
                      disabled={uploading}
                      style={{ margin: '16px', width: 'calc(100% - 32px)' }}
                    >
                      {uploading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaCloudUploadAlt />}
                      {uploading ? "Uploading..." : `Upload ${files.length} file(s)`}
                    </button>
                  </div>
                )}

                {/* Folders Section */}
                <div className="folders-section-premium">
                  <div className="section-title">
                    <FaFolder /> <span>All Folders</span>
                  </div>
                  {loading ? (
                    <div className="loading-premium">
                      <div className="loading-spinner-premium"></div>
                      <p>Loading your files...</p>
                    </div>
                  ) : filteredFolders.length === 0 ? (
                    <div className="empty-state-premium">
                      <FaFolder size={48} />
                      <p>{searchTerm ? "No matching folders found" : "No folders created yet"}</p>
                      {!searchTerm && (
                        <button
                          className="premium-btn btn-primary"
                          onClick={() => document.getElementById('premium-folder-modal').classList.add('show')}
                          style={{ marginTop: 16 }}
                        >
                          Create your first folder
                        </button>
                      )}
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="folders-grid-premium">
                      {filteredFolders.map(folder => (
                        <div
                          key={folder.id}
                          className="folder-card-premium"
                          onClick={() => setSelectedFolder(folder.id)}
                        >
                          <FaFolder className="folder-icon-premium" />
                          <div className="folder-info-premium">
                            <h5>{folder.name}</h5>
                            <p>{folder.files?.length || 0} items</p>
                          </div>
                          <div className="folder-actions-premium">
                            <button
                              className="icon-btn download"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadFolder(folder.id, folder.name);
                              }}
                            >
                              <FaDownload />
                            </button>
                            <button
                              className="icon-btn delete"
                              onClick={(e) => handleDeleteFolder(e, folder.id, folder.name)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="premium-table-wrapper">
                      <table className="premium-table">
                        <thead>
                          <tr>
                            <th>Folder Name</th>
                            <th>Files</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFolders.map(folder => (
                            <tr key={folder.id} onClick={() => setSelectedFolder(folder.id)}>
                              <td data-label="Folder Name">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <FaFolder style={{ color: '#eab308' }} />
                                  <span style={{ fontWeight: 500 }}>{folder.name}</span>
                                </div>
                              </td>
                              <td data-label="Files">{folder.files?.length || 0}</td>
                              <td data-label="Actions">
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button
                                    className="icon-btn download"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadFolder(folder.id, folder.name);
                                    }}
                                  >
                                    <FaDownload />
                                  </button>
                                  <button
                                    className="icon-btn delete"
                                    onClick={(e) => handleDeleteFolder(e, folder.id, folder.name)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Files Section */}
                {selectedFolder && selectedFolderData && (
                  <div className="files-section-premium">
                    <div className="files-header-premium">
                      <div className="section-title">
                        <FaFolder /> <span>{selectedFolderData.name}</span>
                      </div>
                      <button
                        className="premium-btn btn-outline"
                        onClick={() => setExpandedFolders(prev => ({ ...prev, [selectedFolder]: !prev[selectedFolder] }))}
                      >
                        {expandedFolders[selectedFolder] ? "Collapse" : "Expand"}
                      </button>
                    </div>

                    {selectedFolderData.files?.length === 0 ? (
                      <div className="empty-state-premium">
                        <FaFileAlt size={48} />
                        <p>This folder is empty</p>
                        <button
                          className="premium-btn btn-success"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ marginTop: 16 }}
                        >
                          Upload files
                        </button>
                      </div>
                    ) : viewMode === 'grid' ? (
                      <div className="files-grid-premium">
                        {selectedFolderData.files.map(file => (
                          <div key={file.id} className="file-card-premium">
                            <div className="file-icon-premium">
                              {getFileIcon(file.file_name)}
                            </div>
                            <h4 title={file.file_name}>
                              {file.file_name.length > 20 ? file.file_name.substring(0, 20) + '...' : file.file_name}
                            </h4>
                            <p>{formatFileSize(file.file_size)}</p>
                            <div className="file-actions-premium">
                              <button className="icon-btn" onClick={() => window.open(file.url, '_blank')}>
                                <FaEye />
                              </button>
                              <button className="icon-btn download" onClick={() => handleDownloadFile(file.id, file.file_name)}>
                                <FaDownload />
                              </button>
                              <button className="icon-btn delete" onClick={() => handleDeleteFile(file.id, file.file_name)}>
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="premium-table-wrapper">
                        <table className="premium-table">
                          <thead>
                            <tr>
                              <th>File Name</th>
                              <th>Size</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedFolderData.files.map(file => (
                              <tr key={file.id}>
                                <td data-label="File Name">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {getFileIcon(file.file_name)}
                                    <span>{file.file_name}</span>
                                  </div>
                                </td>
                                <td data-label="Size">{formatFileSize(file.file_size)}</td>
                                <td data-label="Actions">
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="icon-btn" onClick={() => window.open(file.url, '_blank')}>
                                      <FaEye />
                                    </button>
                                    <button className="icon-btn download" onClick={() => handleDownloadFile(file.id, file.file_name)}>
                                      <FaDownload />
                                    </button>
                                    <button className="icon-btn delete" onClick={() => handleDeleteFile(file.id, file.file_name)}>
                                      <FaTrash />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Premium Modal */}
            <div id="premium-folder-modal" className="premium-modal" onClick={(e) => {
              if (e.target === e.currentTarget) e.currentTarget.classList.remove('show');
            }}>
              <div className="modal-content-premium">
                <h5>Create New Folder</h5>
                <input
                  type="text"
                  placeholder="Enter folder name..."
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateFolder(e);
                      document.getElementById('premium-folder-modal').classList.remove('show');
                    }
                  }}
                />
                <div className="modal-actions-premium">
                  <button
                    className="premium-btn btn-outline"
                    onClick={() => document.getElementById('premium-folder-modal').classList.remove('show')}
                  >
                    Cancel
                  </button>
                  <button
                    className="premium-btn btn-primary"
                    onClick={async (e) => {
                      await handleCreateFolder(e);
                      document.getElementById('premium-folder-modal').classList.remove('show');
                    }}
                    disabled={creatingFolder}
                  >
                    {creatingFolder ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : "Create Folder"}
                  </button>
                </div>
              </div>
            </div>

            {/* Delete Folder / File Confirmation Modal (Default "No" Ticked) */}
            <DeleteFolderModal
              isOpen={deleteModal.isOpen}
              onClose={() => setDeleteModal({ isOpen: false, id: null, name: "", type: "Folder", loading: false })}
              onConfirm={handleConfirmDelete}
              folderName={deleteModal.name}
              itemType={deleteModal.type}
              loading={deleteModal.loading}
            />
          </div>
        );
      };

      export default MangerLayer;
